
import { useCallback, useState } from 'react';
import { Poll } from '@/types/poll';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugVoteFlow } from '@/utils/debug';
import { useAnonymousVoting } from '@/components/polls/shared/poll-display/useAnonymousVoting';

export interface UseUniversalPollVotingConfig {
  polls: Poll[];
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>;
  userVotes: Record<string, number>;
  setUserVotes: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  markRecentVote?: (pollId: string) => void;
  onRefresh?: () => void;
}

export const useUniversalPollVoting = (config: UseUniversalPollVotingConfig) => {
  const { isAuthenticated } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get anonymous voting context - but only use it for the first poll to avoid hook order issues
  const firstPoll = config.polls[0];
  const {
    submitAnonymousVote
  } = useAnonymousVoting(firstPoll?.id, isAuthenticated);

  const canVote = useCallback((pollId: string): boolean => {
    if (isProcessing) return false;
    
    const poll = config.polls.find(p => p.id === pollId);
    if (!poll) return false;

    // Check if user has already voted
    if (config.userVotes[pollId] !== undefined) {
      return false;
    }

    // For authenticated users, they can vote if the poll allows it
    if (isAuthenticated) {
      return poll.status === 'live';
    }

    // For anonymous users, they can vote if anonymous voting is allowed
    return poll.anonymous_allowed === true && poll.status === 'live';
  }, [isAuthenticated, isProcessing, config.polls, config.userVotes]);

  const handleVote = useCallback(async (pollId: string, optionId: number): Promise<boolean> => {
    if (isProcessing) {
      toast.error('Please wait, processing your previous vote...');
      return false;
    }

    const poll = config.polls.find(p => p.id === pollId);
    if (!poll) {
      toast.error('Poll not found');
      return false;
    }

    if (!canVote(pollId)) {
      toast.error('You cannot vote on this poll');
      return false;
    }

    setIsProcessing(true);
    debugVoteFlow('UNIVERSAL_VOTE_START', { pollId, optionId, isAuthenticated });

    try {
      // Mark as recent vote to prevent double counting from real-time
      config.markRecentVote?.(pollId);

      if (isAuthenticated) {
        // Authenticated vote
        const { error } = await supabase.rpc('insert_poll_vote', {
          input_poll_id: pollId,
          input_user_id: (await supabase.auth.getUser()).data.user?.id || '',
          input_option_id: optionId
        });

        if (error) {
          debugVoteFlow('AUTHENTICATED_VOTE_ERROR', { error });
          throw error;
        }

        // Update local state for authenticated users
        config.setUserVotes(prev => ({
          ...prev,
          [pollId]: optionId
        }));

        debugVoteFlow('AUTHENTICATED_VOTE_SUCCESS', { pollId, optionId });
        toast.success('Your vote has been recorded!');
      } else {
        // Anonymous vote - only for the first poll to avoid hook issues
        if (pollId === firstPoll?.id) {
          const success = await submitAnonymousVote(optionId);
          if (success) {
            debugVoteFlow('ANONYMOUS_VOTE_SUCCESS', { pollId, optionId });
          } else {
            throw new Error('Failed to submit anonymous vote');
          }
        } else {
          throw new Error('Anonymous voting only supported for active poll');
        }
      }

      // Optimistically update poll counts
      config.setPolls(currentPolls => currentPolls.map(p => {
        if (p.id === pollId) {
          const updatedOptions = p.options.map(option => {
            if (option.id === optionId) {
              return {
                ...option,
                votes: option.votes + 1
              };
            }
            return option;
          });

          const newTotalVotes = p.total_votes + 1;
          const optionsWithPercentages = updatedOptions.map(option => ({
            ...option,
            percentage: newTotalVotes > 0 ? Math.round((option.votes / newTotalVotes) * 100) : 0
          }));

          return {
            ...p,
            options: optionsWithPercentages,
            total_votes: newTotalVotes,
            questions: {
              ...p.questions,
              options: optionsWithPercentages,
              total_votes: newTotalVotes
            }
          };
        }
        return p;
      }));

      // Refresh after a delay to get accurate counts
      if (config.onRefresh) {
        setTimeout(() => {
          config.onRefresh?.();
        }, 1000);
      }

      return true;
    } catch (error) {
      debugVoteFlow('UNIVERSAL_VOTE_ERROR', { error });
      console.error('Error submitting vote:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('already voted')) {
        toast.error('You have already voted on this poll');
        // Update local state to reflect that user has voted
        config.setUserVotes(prev => ({
          ...prev,
          [pollId]: optionId
        }));
      } else {
        toast.error('Failed to submit vote. Please try again.');
      }
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [
    isAuthenticated,
    isProcessing,
    config,
    canVote,
    submitAnonymousVote,
    firstPoll?.id
  ]);

  return {
    handleVote,
    canVote,
    isProcessing
  };
};
